import React, { ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "src/lib/utils";
import MerciSlogan from "src/components/Slogan/MerciSlogan";
import Hero from "src/assets/Hero.jpg";
import HeroCover from "src/assets/HeroCover.png";
import ArrowLeft from "src/assets/cards/ArrowLeft.png";
import ArrowRight from "src/assets/cards/ArrowRight.png";
import DividerHeart from "src/assets/DividerHeart.png";
import Save from "src/assets/share/Save.png";
import Copy from "src/assets/share/Copy.png";
import Reset from "src/assets/share/Reset.png";
import TypingAnimation from "src/assets/TypingAnimation.gif";
import Design1 from "src/assets/cards/Design1.png";
import Design2 from "src/assets/cards/Design2.png";
import Design3 from "src/assets/cards/Design3.png";
import Guide1 from "src/assets/guide/guide-1.png";
import Guide2 from "src/assets/guide/guide-2.png";
import Guide3 from "src/assets/guide/guide-3.png";
import Guide4 from "src/assets/guide/guide-4.png";
import Guide5 from "src/assets/guide/guide-5.png";
import {
  Select,
  SelectTrigger as SelectTriggerControl,
  SelectContent,
  SelectItem,
  SelectValue,
} from "src/components/ui/select";
import { Input } from "src/components/ui/input";
import { Slider } from "src/components/ui/slider";
import { Button } from "./components/ui/button";
import { Section } from "./components/ui/section";
import { useGetGreeting } from "./hooks/useGetGreeting";
import { TypeAnimation } from "react-type-animation";
import { passedBlacklist } from "./lib/passedBlacklist";
import { shareImage } from "./lib/share";
import { showThankYouFor } from "./lib/showThankYouFor";
import SectionHeadline from "./components/ui/SectionHeadline";
import Container from "./components/ui/Container";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import useGetHeaderSize from "./hooks/useGetHeaderSize";
import ShopPopup from "./components/ShopPopup/ShopPopup";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipArrow,
} from "./components/ui/tooltip";
import Modal from "./components/Legal/Modal";
import Terms from "./components/Legal/Terms";
import { NavLink } from "react-router";
import { ScrollUp } from "./components/ui/ScrollUp";

const SelectTrigger = ({
  isSelected,
  children,
}: {
  isSelected: boolean;
  children: ReactNode;
}) => {
  return (
    <SelectTriggerControl
      className={cn(isSelected && "bg-accent text-white border-accent")}
    >
      {children}
    </SelectTriggerControl>
  );
};

export interface FormInterface {
  salutation: string;
  name: string;
  relationship: string;
  thankYouFor: string;
  familiar: number;
  funny: number;
  epic: number;
  modern: number;
}

const isValid = (values: FormInterface): boolean => {
  if (values.salutation === "") {
    return false;
  }
  if (values.name === "" || values.name.length > 50) {
    return false;
  }
  if (values.relationship === "") {
    return false;
  }
  if (countWords(values.name) > 2) {
    return false;
  }
  return values.thankYouFor !== "";
};

const apiUrl = process.env.REACT_APP_API_URL;
const apiToken = process.env.REACT_APP_API_TOKEN;
const companyBrandId = process.env.REACT_APP_COMPANY_BRAND_ID;

if (
  apiUrl === undefined ||
  apiToken === undefined ||
  companyBrandId === undefined
) {
  throw new Error("missing configuration.");
}

const THANK_YOUS = [
  "deine Hilfe",
  "deine Arbeit",
  "deinen Ratschlag",
  "deine Einladung",
  "deine Vertretung",
  "deine Zeit",
  "dein offenes Ohr",
  "deine Loyalität",
  "die kleinen Dinge",
  "deine Liebe",
  "deine Vergebung",
  "deine Unterstützung",
  "deine Fürsorge",
  "das Paketeannehmen",
  "das Blumengießen",
  "die Einladung",
  "Dich!",
];

const headers = {
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `${apiToken}`,
};

const countWords = (phrase: string) => {
  const wordMatch = phrase.trim().match(/\s/g);
  return wordMatch === null ? 1 : wordMatch.length + 1;
};

function App() {
  const greeting = useGetGreeting();
  const [values, setValues] = useState<FormInterface>({
    salutation: "",
    name: "",
    relationship: "",
    thankYouFor: "",
    familiar: 50,
    funny: 50,
    epic: 50,
    modern: 50,
  });
  const [tooltipOpen, setTooltipOpen] = useState(true);
  const [generatedMessage, setGeneratedMessage] = useState<null | string>(null);
  const [generatedImage, setGeneratedImage] = useState<
    null | { version: number; file: Blob; message: string }[]
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [error, setError] = useState<{
    isError: boolean;
    type: "capacity" | "api" | "blacklist" | null;
  }>({
    isError: false,
    type: null,
  });
  const [background, setBackground] = useState(0);
  const [isShopPopupOpen, setIsShopPopupOpen] = useState(false);
  const [isBlinking, setIsBlinking] = useState(true);

  const { showFullHeader } = useGetHeaderSize();
  const messageRef = useRef<HTMLDivElement>(null);

  // todo:
  // - outsource handleGenerate to somewhere

  useEffect(() => {
    if (isBlinking) {
      setTimeout(() => setIsBlinking(false), 600);
    }
  }, [isBlinking]);

  const steps = [0, 1, 2, 3];

  const loadImage = async () => {
    const foundImage = generatedImage?.find(
      (image) => image.version === background,
    );
    if (
      (foundImage !== undefined && foundImage.message === generatedMessage) ||
      generatedMessage === null
    ) {
      return foundImage?.file;
    }
    setIsShareLoading(true);
    const response = await fetch(
      `${apiUrl}/company-brand-thank-you-messages/download${background === 0 ? "" : `/${background}`}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          companyBrandId,
          message: generatedMessage,
        }),
      },
    );

    if (!response.ok) {
      setIsLoading(false);
      return;
    }

    const imageBlob = await response.blob();

    if (generatedImage === null) {
      setGeneratedImage([
        { version: background, file: imageBlob, message: generatedMessage },
      ]);
      setIsShareLoading(false);

      return imageBlob;
    }

    setGeneratedImage(
      generatedImage
        .filter((image) => image.version !== background)
        .concat([
          { version: background, file: imageBlob, message: generatedMessage },
        ]),
    );
    setIsShareLoading(false);

    return imageBlob;
  };

  useEffect(() => {
    if (!tooltipOpen) {
      return;
    }
    const handleClick = () => {
      setTooltipOpen(false);
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [tooltipOpen]);

  const handleGenerate = async (
    count?: number,
    doesAlternativeCheck?: boolean,
  ) => {
    setError({
      isError: false,
      type: null,
    });
    const stringFromParams = Object.keys(values).map(
      (key) => `${values[key as keyof FormInterface]} `,
    );
    const { passed } = passedBlacklist(stringFromParams.join(" "));

    if (!passed) {
      setError({
        isError: true,
        type: "blacklist",
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(
        `${apiUrl}/company-brand-thank-you-messages/generate`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            companyBrandId,
            params: {
              salutation: values.salutation,
              name: values.name,
              relationship: values.relationship,
              thankYouFor: values.thankYouFor,
              familiar: `${values.familiar}`,
              funny: `${values.funny}`,
              epic: `${values.epic}`,
              modern: `${values.modern}`,
            },
          }),
        },
      );
      if (!response.ok) {
        switch (response.status) {
          case 429:
            setError({
              isError: true,
              type: "capacity",
            });
            break;
          case 403:
          case 400:
            setError({
              isError: true,
              type: "blacklist",
            });
            break;
          default:
            setError({
              isError: true,
              type: "api",
            });
        }
        setIsLoading(false);
        return;
      }
      const text = (await response.text()).trim();
      const { passed } = passedBlacklist(text);
      if (!passed) {
        if (count === undefined || count < 2) {
          await handleGenerate(count === undefined ? 1 : count + 1);
          return;
        }
        setError({
          isError: true,
          type: "blacklist",
        });
        setIsLoading(false);
        return;
      }
      const { passed: passedAlternative } = passedBlacklist(text);
      if (!passedAlternative) {
        if (doesAlternativeCheck) {
          setError({
            isError: true,
            type: "blacklist",
          });
          setIsLoading(false);
          return;
        }
        await handleGenerate(0, true);
        return;
      }
      setGeneratedMessage(
        text.replace(/\n/g, " ").replace(/\smerci, dass/, "\n\nmerci, dass"),
      );
    } catch (e) {
      setError({
        isError: true,
        type: "api",
      });
    }
    setIsLoading(false);
    const scrollTo = messageRef.current?.offsetTop ?? 0;
    window.scrollTo({
      left: 0,
      top: scrollTo > 0 ? scrollTo - (showFullHeader ? 100 : 60) : scrollTo,
      behavior: "smooth",
    });
  };

  const handleCopyText = async () => {
    if (generatedMessage === null) {
      return;
    }
    await navigator.clipboard.writeText(generatedMessage);
    setIsBlinking(true);
  };

  const handleShare = async () => {
    if (generatedMessage === null) {
      return;
    }

    const loadedImage =
      generatedImage?.find((image) => image.version === background)?.file ??
      (await loadImage());
    if (loadedImage === undefined) {
      return;
    }
    await shareImage(loadedImage, values.name);
  };

  useEffect(() => {
    if (showThankYouFor(values.thankYouFor, values)) {
      return;
    }
    setValues({
      ...values,
      thankYouFor: "",
    });
  }, [values.relationship]);

  useEffect(() => {
    if (generatedMessage === null) {
      return;
    }
    void loadImage();
  }, [generatedMessage, background]);

  const handlePreviousBackground = () => {
    if (background === 0) {
      setBackground(3);
      return;
    }
    setBackground(background - 1);
  };
  const handleNextBackground = () => {
    if (background === 3) {
      setBackground(0);
      return;
    }
    setBackground(background + 1);
  };

  const getBackgroundValue = () => {
    if (background === 0) {
      return "";
    }
    if (background === 1) {
      return `url(${Design1})`;
    }
    if (background === 2) {
      return `url(${Design2})`;
    }
    if (background === 3) {
      return `url(${Design3})`;
    }
  };

  return (
    <div className="min-h-screen bg-background text-center">
      <Header />
      {isShopPopupOpen && (
        <ShopPopup handleClose={() => setIsShopPopupOpen(false)} />
      )}
      <div className="flex flex-col items-center w-full">
        <div
          className={cn("w-full", showFullHeader ? "mt-[100px]" : "mt-[60px]")}
        >
          <img
            src={Hero}
            alt="Die herzliche Intelligenz Banner"
            className="w-full h-[200px] object-cover md:h-auto"
          />
        </div>
        <div className="h-[150px] mt-[-60px] md:h-[200px] md:mt-[-110px]">
          <img className="max-h-full" src={HeroCover} alt="Happy People" />
        </div>
        <Container className="py-10">
          Dir fehlen die Worte, wenn es darum geht, „Danke“ zu sagen? Die
          Herzliche Intelligenz hilft dir dabei, eine persönliche
          Dankesbotschaft zu verfassen. Los geht's!
        </Container>
        <Container className="flex flex-col gap-4 md:grid-cols-2 md:grid lg:grid-cols-none lg:flex lg:flex-row mb-10 justify-stretch">
          <div className="flex flex-1 flex-col items-center">
            <div className="flex-1">
              <img src={Guide1} alt="Guide 1" className="h-[130px]" />
            </div>
            <div className="">Empfänger wählen und Danke personalisieren.</div>
          </div>
          <div className="flex flex-1 flex-col gap-2 items-center">
            <div className="flex-1">
              <img src={Guide2} alt="Guide 2" className="h-[130px]" />
            </div>
            <div className="">
              Slider verschieben, um den Schreibstil anzupassen.
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-2 items-center">
            <div className="flex-1">
              <img src={Guide3} alt="Guide 3" className="h-[130px]" />
            </div>
            <div className="">Danke generieren und kurz abwarten.</div>
          </div>
          <div className="flex flex-1 flex-col gap-2 items-center">
            <div className="flex-1">
              <img src={Guide4} alt="Guide 4" className="h-[130px]" />
            </div>
            <div className="">Grußkarten-Design auswählen.</div>
          </div>
          <div className="flex flex-1 flex-col gap-2 items-center">
            <div className="flex-1">
              <img src={Guide5} alt="Guide 5" className="h-[130px]" />
            </div>
            <div className="">
              Herunterladen und mit deinen Liebsten teilen.
            </div>
          </div>
        </Container>
        <div
          ref={messageRef}
          // @ts-ignore
          style={{ "--image-url": getBackgroundValue() }}
          className={cn(
            "bg-secondary-foreground w-full flex justify-center bg-bottom text-white bg-cover bg-[image:var(--image-url)]",
          )}
        >
          <div className={cn("w-full flex flex-col items-center px-2")}>
            <div className="w-[120px] h-[80px] flex flex-col justify-center">
              <MerciSlogan background={background} />
            </div>
            <div className="flex items-center gap-2 max-w-[512px]">
              <div className="w-[40px]">
                {generatedMessage !== null && (
                  <div
                    onClick={handlePreviousBackground}
                    className="w-[40px] h-[40px] flex justify-center"
                  >
                    <img
                      src={ArrowLeft}
                      className="absolute h-[40px] cursor-pointer brightness-0 blur-sm opacity-50"
                      alt="Vorheriger Hintergrund"
                    />
                    <img
                      src={ArrowLeft}
                      className="absolute h-[40px] cursor-pointer"
                      alt="Vorheriger Hintergrund"
                    />
                  </div>
                )}
              </div>
              <div
                className={cn(
                  "flex-1 bg-[#fde8e9] text-center text-secondary font-script text-4xl p-6 rounded-2xl shadow-2xl leading-[1.875rem] hyphens-auto",
                  showFullHeader && "text-5xl",
                  (background === 1 || background === 2) &&
                    "bg-opacity-100 bg-white",
                )}
              >
                {isLoading ? (
                  <img
                    src={TypingAnimation}
                    className="opacity-30 w-[100px]"
                    alt="Ladebalken"
                  />
                ) : (
                  <>
                    {generatedMessage === null ? (
                      <>
                        <TypeAnimation
                          wrapper="div"
                          key={2}
                          sequence={[greeting]}
                          speed={85}
                          cursor={false}
                        />
                      </>
                    ) : (
                      <div className="flex flex-col gap-4 whitespace-pre-line">
                        <TypeAnimation
                          wrapper="div"
                          key={1}
                          sequence={[
                            generatedMessage,
                            () => {
                              setTooltipOpen(true);
                            },
                          ]}
                          speed={85}
                          cursor={false}
                        />
                        <div className="w-full flex justify-end items-center gap-4">
                          <div
                            onClick={isShareLoading ? undefined : handleShare}
                            className={cn(
                              "cursor-pointer rounded-full h-12 bg-[length:35px] border-secondary flex items-center justify-center",
                              isShareLoading && "opacity-10 cursor-default",
                            )}
                          >
                            {isShareLoading ? (
                              <img
                                src={Reset}
                                alt="Lädt..."
                                className="h-[20px] animate-reverse-spin"
                              />
                            ) : (
                              <img
                                src={Save}
                                alt="Teilen / Speichern"
                                className="h-[20px]"
                              />
                            )}
                          </div>
                          <div
                            onClick={() => handleCopyText()}
                            className={cn(
                              "cursor-pointer rounded-full h-12 bg-[length:35px] border-secondary flex items-center justify-center",
                              isBlinking &&
                                "animate-[blink_300ms_ease-in-out_infinite]",
                            )}
                          >
                            <img
                              src={Copy}
                              alt="Text in die Zwischenablage kopieren"
                              className="h-[20px]"
                            />
                          </div>
                          <div
                            onClick={() => handleGenerate()}
                            className="cursor-pointer rounded-full h-12 bg-[length:35px] border-secondary flex items-center justify-center"
                          >
                            <img
                              src={Reset}
                              alt="Neu generieren"
                              className="h-[20px]"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="w-[40px]">
                {generatedMessage !== null && (
                  <TooltipProvider>
                    <Tooltip open={tooltipOpen}>
                      <TooltipTrigger>
                        <div
                          onClick={handleNextBackground}
                          className="w-[40px] h-[40px] flex justify-center"
                        >
                          <img
                            src={ArrowRight}
                            className="absolute h-[40px] cursor-pointer brightness-0 blur-sm opacity-50"
                            alt="Vorheriger Hintergrund"
                          />
                          <img
                            src={ArrowRight}
                            className="absolute h-[40px] cursor-pointer"
                            alt="Vorheriger Hintergrund"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        className="bg-white border-none text-black cursor-pointer"
                        onClickCapture={() => {
                          setTooltipOpen(false);
                        }}
                        onClick={() => {
                          //setTooltipOpen(false)}
                        }}
                      >
                        Wähle dein
                        <br />
                        Design aus!
                        <TooltipArrow className="fill-white" />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            <div
              className={cn(
                "after:content-[''] after:absolute after:ml-[-10px] after:left-[50%] after:h-0 after:w-0 after:border-x-[10px] after:border-x-transparent after:border-t-[10px] after:border-solid after:z-[8] after:-mt-[1px] after:border-t-white",
                (background === 3 || background === 0) &&
                  "after:border-t-[#fde8e9]",
              )}
            />
            <div className={cn("h-28", generatedMessage === null && "h-20")} />
          </div>
        </div>
        <div
          className={cn(
            "w-[180px] -mt-[80px] mb-5",
            generatedMessage === null && "-mt-[130px]",
          )}
        >
          {generatedMessage !== null ? (
            <div className="flex flex-col justify-center absolute w-[180px] text-center">
              <div className="flex justify-center gap-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      //setTooltipOpen(false);
                      setBackground(step);
                    }}
                    className={cn(
                      "h-3 w-3 rounded-full cursor-pointer",
                      background === step
                        ? "bg-white"
                        : background === 0 || background === 3
                          ? "bg-white/50"
                          : "bg-gray-400/50",
                    )}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="h-14" />
          )}
        </div>
        <div className="h-20" />
        <div className="w-[140px] mt-[-70px] mb-5">
          <img src={DividerHeart} alt="merci Herz" />
        </div>
        <Container className="mx-5 flex flex-col gap-6">
          <div>
            <SectionHeadline>1. Empfänger*in wählen</SectionHeadline>
            <Section>
              <div className="flex gap-2 text-accent">
                <Select
                  disabled={isLoading}
                  autoComplete="false"
                  value={values.salutation}
                  onValueChange={(salutation) =>
                    setValues({ ...values, salutation })
                  }
                >
                  <SelectTrigger isSelected={values.salutation !== ""}>
                    <SelectValue
                      placeholder="Anrede"
                      className="text-accent placeholder:text-accent"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="männlich">Lieber</SelectItem>
                    <SelectItem value="weiblich">Liebe</SelectItem>
                    <SelectItem value="neutral">Liebe*r</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  disabled={isLoading}
                  placeholder="Name"
                  autoComplete="false"
                  maxLength={50}
                  value={values.name}
                  onChange={(e) =>
                    setValues({ ...values, name: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Select
                  autoComplete="false"
                  value={values.relationship}
                  disabled={isLoading}
                  onValueChange={(relationship) =>
                    setValues({ ...values, relationship })
                  }
                >
                  <SelectTrigger isSelected={values.relationship !== ""}>
                    <SelectValue
                      placeholder="Wir sind ..."
                      className="text-accent placeholder:text-accent"
                    >
                      Wir sind {values.relationship}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bekannte">Bekannte</SelectItem>
                    <SelectItem value="Kollegen">Kolleg*innen</SelectItem>
                    <SelectItem value="Freunde">Freund*innen</SelectItem>
                    <SelectItem value="Verliebte">Verliebte</SelectItem>
                    <SelectItem value="Familie">Familie</SelectItem>
                    <SelectItem value="Nachbarn">Nachbar*innen</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  autoComplete="false"
                  value={values.thankYouFor}
                  disabled={isLoading || values.relationship === ""}
                  onValueChange={(thankYouFor) =>
                    setValues({ ...values, thankYouFor })
                  }
                >
                  <SelectTrigger isSelected={values.thankYouFor !== ""}>
                    <SelectValue
                      placeholder="Danke für ..."
                      className="text-accent placeholder:text-accent"
                    >
                      Danke für {values.thankYouFor}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {THANK_YOUS.map((thankYou) => (
                      <React.Fragment key={thankYou}>
                        {showThankYouFor(thankYou, values) && (
                          <SelectItem value={thankYou}>{thankYou}</SelectItem>
                        )}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Section>
          </div>
          <div>
            <SectionHeadline>2. Schreibstil wählen</SectionHeadline>
            <Section>
              <div className="flex  flex-col gap-1">
                <Slider
                  disabled={isLoading}
                  defaultValue={[values.familiar]}
                  onValueChange={([familiar]) => {
                    setValues({
                      ...values,
                      familiar,
                    });
                  }}
                  max={100}
                  step={1}
                  name="familiar"
                />
                <div className="flex text-muted-foreground text-xs">
                  <div>Seriös</div>
                  <div className="flex-1 text-end">Vertraut</div>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Slider
                  disabled={isLoading}
                  defaultValue={[values.funny]}
                  onValueChange={([funny]) => {
                    setValues({
                      ...values,
                      funny,
                    });
                  }}
                  max={100}
                  step={1}
                  name="funny"
                />
                <div className="flex text-muted-foreground text-xs">
                  <div>Herzlich</div>
                  <div className="flex-1 text-end">Lustig</div>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Slider
                  disabled={isLoading}
                  defaultValue={[values.epic]}
                  onValueChange={([epic]) => {
                    setValues({
                      ...values,
                      epic,
                    });
                  }}
                  max={100}
                  step={1}
                  name="epic"
                />
                <div className="flex text-muted-foreground text-xs">
                  <div>Alltäglich</div>
                  <div className="flex-1 text-end">Episch</div>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Slider
                  disabled={isLoading}
                  defaultValue={[values.modern]}
                  onValueChange={([modern]) => {
                    setValues({
                      ...values,
                      modern,
                    });
                  }}
                  max={100}
                  step={1}
                  name="modern"
                />
                <div className="flex text-muted-foreground text-xs">
                  <div>Traditionell</div>
                  <div className="flex-1 text-end">Modern</div>
                </div>
              </div>
            </Section>
          </div>
          <div className="flex justify-center">
            {isLoading && (
              <img
                src={TypingAnimation}
                className="opacity-30 w-[100px]"
                alt="Ladebalken"
              />
            )}
          </div>
          <div>
            <Button
              disabled={!isValid(values) || isLoading}
              onClick={() => handleGenerate()}
            >
              Danke generieren!
            </Button>
            {error.isError && (
              <div className="text-red-500 text-xs my-2">
                {error.type === "capacity" &&
                  "Die maximale Anzahl an generierten Dankesbotschaften wurde erreicht. Bitte versuche es in einer Minute noch einmal."}
                {error.type === "blacklist" &&
                  "Der eingegebene Name enthält Wörter, die nicht verwendet werden dürfen."}
                {error.type === "api" &&
                  "Es scheint Probleme mit der Verbindung zur Herzlichen Intelligenz zu geben. Bitte versuche es später erneut."}
              </div>
            )}
          </div>
        </Container>
      </div>
      <div className="flex py-10 justify-center gap-2">
        <Container className="flex flex-col gap-2">
          <div>
            <span className="font-bold">Hinweis:</span> Unsere herzlichen
            Dankesbotschaften werden mit Unterstützung von künstlicher
            Intelligenz erstellt.
          </div>
          <div>
            Mit der Nutzung dieser Webseite erklärst du dich mit unseren{" "}
            <Modal title="Teilnahmebedingungen">
              <Terms />
            </Modal>{" "}
            einverstanden.
            <br />
            Allgemeine Informationen zum Datenschutz findest du unter{" "}
            <NavLink
              to="/datenschutz"
              className="text-muted-foreground hover:text-secondary-foreground active:text-cta-active focus:text-cta-click underline underline-offset-4"
            >
              Datenschutz
            </NavLink>
            .
          </div>
        </Container>
      </div>
      <div className="flex my-16 justify-center">
        <div
          className="group h-[69px] flex items-center flex-col text-black font-aracne text-[23px] hover:text-secondary-foreground cursor-pointer"
          onClick={() => {
            window.scrollTo({
              left: 0,
              top: 0,
              behavior: "smooth",
            });
          }}
        >
          <ScrollUp />
          <span className="group-hover:underline leading-none mt-[5px] underline-offset-4 group-hover:mt-[12px] transition-all duration-250">
            Zum Seitenanfang
          </span>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default App;
